import type {HotelDetails} from '@/api/hoteldetails';
import type {SortParams} from '@/modules/common/context/QueryParams/models';
import type {
  EventAction,
  EventHandler,
  HotelListEcommerceItem,
  HotelListItem,
} from '@/redux/middleware/googleTagManager/types';
import type {AppDispatch, GetState} from '@/redux/typesNew';
import type {LooseString} from '@/types';

import {setConversionData} from '@find/sponsored-ad-tracking';

import {createHotelDetailsQueryOptions} from '@/api/hoteldetails';
import {queryClient} from '@/queryClient';
import {EVENT_SELECT_HOTEL_ITEM} from '@/redux/middleware/googleTagManager/actionTypes';
import {mapHotelDataToEcommerceItem} from '@/redux/middleware/googleTagManager/utils/hotelData';
import {selectHotelById} from '@/redux/v1/hotels/list/reducer';

type Payload = {
  sortParams: SortParams;
  hotel: HotelListItem;
  isMap: boolean;
  isGallery: boolean;
};

type Action = EventAction<Payload>;

type NullEvent = {
  ecommerce: null;
};

type Event = {
  event: 'select_item';
  event_action: string;
  non_interaction: false;
  ad_url: LooseString<'0'>;
  is_ad: '1 - sponsored' | '0';
  ecommerce: {
    items: Array<HotelListEcommerceItem>;
  };
};

export const action =
  ({
    hotelId,
    position,
    isMap,
    isGallery,
    sortParams,
  }: {
    hotelId: string;
    position: number;
    isMap: boolean;
    isGallery: boolean;
    sortParams: SortParams;
  }) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const hotel = selectHotelById(hotelId, state);

    let data: HotelDetails | undefined = undefined;
    try {
      data = await queryClient.fetchQuery(
        createHotelDetailsQueryOptions(hotelId)
      );
    } catch {
      // noop, we can deal with empty hotel details
    }

    dispatch({
      type: EVENT_SELECT_HOTEL_ITEM,
      payload: {
        isMap,
        isGallery,
        sortParams,
        hotel: {
          id: hotel?.id,
          price: hotel?.totalPrice,
          specials: hotel?.tourOperatorSpecials,
          position,
          data,
          sponsored: hotel?.sponsored,
        },
      },
    });
  };

export const handler: EventHandler<Action, [] | [NullEvent, Event]> = (
  eventAction
) => {
  const {hotel, sortParams, isMap, isGallery} = eventAction.payload;

  if (hotel.sponsored) {
    try {
      setConversionData(hotel.id, {
        type: 'hotel-listing',
        trackingUrl: hotel.sponsored.conversionUrl,
      });
    } catch {
      // ignore
    }
  }

  return [
    {ecommerce: null},
    {
      event: 'select_item',
      event_action: isGallery ? 'gallery_click_hotel' : 'click_hotel',
      non_interaction: false,
      ad_url: hotel.sponsored?.clickUrl ?? '0',
      is_ad: hotel.sponsored?.clickUrl ? '1 - sponsored' : '0',
      ecommerce: {
        items: [
          mapHotelDataToEcommerceItem(
            hotel,
            String(sortParams.sortBy),
            hotel.data,
            isMap
          ),
        ],
      },
    },
  ];
};
